import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/jobsiq/Navbar";
import Footer from "../components/jobsiq/Footer";
import { Scales, ArrowUp } from "@phosphor-icons/react";

const SECTIONS = [
    {
        id: "acceptance",
        n: "01",
        title: "Acceptance of terms",
        body: (
            <p>
                By accessing or using Jobs IQ ("Platform"), you agree to comply with these Terms
                & Conditions. If you do not agree, please discontinue use immediately.
            </p>
        ),
    },
    {
        id: "eligibility",
        n: "02",
        title: "Eligibility",
        body: (
            <ul className="space-y-2 list-disc pl-5">
                <li>Users must be at least 18 years old or meet the minimum legal working age in their jurisdiction.</li>
                <li>Employers and recruiters must have valid business credentials.</li>
            </ul>
        ),
    },
    {
        id: "accounts",
        n: "03",
        title: "User accounts",
        body: (
            <ul className="space-y-2 list-disc pl-5">
                <li>You are responsible for maintaining the confidentiality of your login credentials.</li>
                <li>You agree to provide accurate, current, and complete information during registration.</li>
                <li>Jobs IQ reserves the right to suspend or terminate accounts for misuse or violation of these terms.</li>
            </ul>
        ),
    },
    {
        id: "usage",
        n: "04",
        title: "Platform usage",
        body: (
            <>
                <ul className="space-y-2 list-disc pl-5">
                    <li><strong>Job seekers:</strong> may upload résumés, apply for jobs, and interact with employers.</li>
                    <li><strong>Employers / recruiters:</strong> may post jobs, access candidate profiles, and use AI-powered matching tools.</li>
                </ul>
                <p className="mt-4 mb-2">Users must <em>not</em>:</p>
                <ul className="space-y-2 list-disc pl-5">
                    <li>Post false or misleading information.</li>
                    <li>Engage in discriminatory, abusive, or unlawful activity.</li>
                    <li>Attempt to disrupt or misuse the platform's services.</li>
                </ul>
            </>
        ),
    },
    {
        id: "ip",
        n: "05",
        title: "Intellectual property",
        body: (
            <ul className="space-y-2 list-disc pl-5">
                <li>All content, algorithms, and technology on Jobs IQ are proprietary and protected by intellectual property laws.</li>
                <li>Users may not copy, modify, distribute, or reverse-engineer any part of the platform without prior written consent.</li>
            </ul>
        ),
    },
    {
        id: "data-privacy",
        n: "06",
        title: "Data & privacy",
        body: (
            <ul className="space-y-2 list-disc pl-5">
                <li>
                    Use of the platform is subject to our{" "}
                    <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link>.
                </li>
                <li>Employers must handle candidate data responsibly and in compliance with applicable laws.</li>
            </ul>
        ),
    },
    {
        id: "payment",
        n: "07",
        title: "Payment & services",
        body: (
            <ul className="space-y-2 list-disc pl-5">
                <li>Certain premium features may require payment.</li>
                <li>Fees are non-refundable unless otherwise stated.</li>
                <li>Jobs IQ reserves the right to modify pricing and service offerings with prior notice.</li>
            </ul>
        ),
    },
    {
        id: "liability",
        n: "08",
        title: "Limitation of liability",
        body: (
            <ul className="space-y-2 list-disc pl-5">
                <li>Jobs IQ provides services "as is" without warranties of any kind.</li>
                <li>We are not liable for employment outcomes, accuracy of job postings, or third-party actions.</li>
            </ul>
        ),
    },
    {
        id: "termination",
        n: "09",
        title: "Termination",
        body: (
            <ul className="space-y-2 list-disc pl-5">
                <li>Jobs IQ may suspend or terminate accounts that violate these terms.</li>
                <li>Users may deactivate their accounts at any time.</li>
            </ul>
        ),
    },
    {
        id: "governing-law",
        n: "10",
        title: "Governing law",
        body: (
            <ul className="space-y-2 list-disc pl-5">
                <li>These Terms are governed by the laws of India.</li>
                <li>Any disputes shall be subject to the jurisdiction of courts in Bengaluru, India.</li>
            </ul>
        ),
    },
    {
        id: "updates",
        n: "11",
        title: "Updates",
        body: (
            <p>
                Jobs IQ may update these Terms periodically. Continued use of the platform after
                updates constitutes acceptance of the revised Terms.
            </p>
        ),
    },
];

const LAST_UPDATED = "February 2026";

export default function Terms() {
    return (
        <div data-testid="terms-page">
            <Navbar />

            <section className="bg-slate-950 text-white relative overflow-hidden">
                <div className="absolute inset-0 grid-lines opacity-30" />
                <div className="relative max-w-[1100px] mx-auto px-6 py-16">
                    <div className="font-mono-plex text-xs uppercase tracking-[0.3em] text-blue-400 inline-flex items-center gap-1.5">
                        <Scales size={14} weight="fill" /> Legal · Terms & Conditions
                    </div>
                    <h1 className="font-display text-5xl sm:text-6xl mt-3 leading-[0.95]">
                        The rules of
                        <br />
                        the road.
                    </h1>
                    <p className="mt-6 text-slate-300 max-w-2xl">
                        Short, fair, and binding. By using Jobs IQ you accept these terms — the
                        same ones that keep the platform safe for every seeker and recruiter on it.
                    </p>
                    <div className="mt-6 font-mono-plex text-xs uppercase tracking-widest text-slate-500">
                        Last updated · {LAST_UPDATED}
                    </div>
                </div>
            </section>

            <section className="max-w-[1100px] mx-auto px-6 py-16 grid md:grid-cols-[220px_1fr] gap-12">
                <aside className="hidden md:block">
                    <div className="sticky top-24">
                        <div className="font-mono-plex text-xs uppercase tracking-widest text-slate-500 mb-3">
                            Contents
                        </div>
                        <ol className="space-y-1.5 text-sm">
                            {SECTIONS.map((s) => (
                                <li key={s.id}>
                                    <a
                                        href={`#${s.id}`}
                                        data-testid={`terms-toc-${s.id}`}
                                        className="text-slate-600 hover:text-blue-600 inline-flex items-baseline gap-2"
                                    >
                                        <span className="font-mono-plex text-[10px] text-slate-400">{s.n}</span>
                                        <span>{s.title}</span>
                                    </a>
                                </li>
                            ))}
                        </ol>
                    </div>
                </aside>

                <article className="space-y-12">
                    {SECTIONS.map((s) => (
                        <section
                            key={s.id}
                            id={s.id}
                            data-testid={`terms-section-${s.id}`}
                            className="scroll-mt-24"
                        >
                            <div className="font-mono-plex text-xs uppercase tracking-widest text-blue-600">
                                {s.n}
                            </div>
                            <h2 className="font-display text-3xl mt-1 leading-tight">{s.title}</h2>
                            <div className="mt-4 text-slate-700 leading-relaxed">{s.body}</div>
                        </section>
                    ))}

                    <div className="border-t border-slate-200 pt-8 flex items-center justify-between gap-4 flex-wrap">
                        <div className="flex items-center gap-4 text-sm">
                            <Link
                                to="/"
                                data-testid="terms-back-home"
                                className="text-blue-600 hover:underline"
                            >
                                ← Back to Jobs IQ
                            </Link>
                            <span className="text-slate-300">·</span>
                            <Link
                                to="/privacy"
                                data-testid="terms-to-privacy"
                                className="text-blue-600 hover:underline"
                            >
                                Read the Privacy Policy
                            </Link>
                        </div>
                        <a
                            href="#top"
                            data-testid="terms-back-top"
                            onClick={(e) => {
                                e.preventDefault();
                                window.scrollTo({ top: 0, behavior: "smooth" });
                            }}
                            className="text-sm inline-flex items-center gap-1.5 px-3 py-1.5 border border-slate-300 hover:bg-slate-950 hover:text-white"
                        >
                            <ArrowUp size={14} /> Back to top
                        </a>
                    </div>
                </article>
            </section>

            <Footer />
        </div>
    );
}
